'use client'

import { useLocale } from 'next-intl'

import { isNilOrEmptyString, useMultiScrollState } from '@/shared/lib'
import { Separator } from '@/shared/ui'

import {
  MobileAdditionalDescription,
  type Props as AdditionalDescriptionProps,
} from './additional-description'
import { MobileFacilities, type Props as FacilitiesProps } from './facilities'
import {
  MobileIntroduction,
  type Props as IntroductionProps,
} from './introduction'
import { MobileLocation, type Props as LocationProps } from './location'
import {
  MobileManagementFee,
  type Props as ManagementFeeProps,
} from './management-fee'
import { MobileProperty, type Props as PropertyProps } from './property'
import { MobileRoomOption, type Props as RoomOptionProps } from './room-option'
import {
  DuplexStructureMessageProps,
  MobileStructure,
  type StructureProps,
  MobileSharedPlaceMessage,
  MobileDuplexStructureMessage,
} from './structure'
import {
  MobileTransportation,
  type Props as TransportationProps,
} from './transportation'
import { MobileUsageGuide, type Props as UsageGuideProps } from './usage-guide'
import { TabValue } from '../../lib/tab'

type Props = StructureProps &
  PropertyProps &
  FacilitiesProps &
  DuplexStructureMessageProps &
  IntroductionProps &
  LocationProps &
  TransportationProps &
  RoomOptionProps &
  UsageGuideProps &
  AdditionalDescriptionProps &
  ManagementFeeProps

export const MobileRoomInformation = ({
  roomCnt,
  bathroomCnt,
  cookroomCnt,
  sittingroomCnt,
  sharedRoom,
  sharedBathroom,
  sharedCookroom,
  propertyType,
  pyeongSize,
  squareMeterSize,
  hasElevator,
  parkingType,
  parkingNotice,
  duplexStructure,
  description,
  addrStreet,
  lat,
  lng,
  subwayStations,
  transportation,
  basicOptions,
  additionalOptions,
  missingOptions,
  usageGuide,
  additionalDescription,
  includeElectricity,
  includeWater,
  includeGas,
  managementFeeDescription,
  includeInternet,
}: Props) => {
  const locale = useLocale()
  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  const isEnglish = locale === 'en'

  const hasUsageGuide = !isNilOrEmptyString(usageGuide)
  const hasAdditionalDescription = !isNilOrEmptyString(additionalDescription)

  return (
    <div
      className="pt-6"
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.ROOM_INFORMATION, element)
        }
      }}
    >
      {isEnglish && (
        <div className="mb-10 px-4">
          <div className="bg-gray-0 typo-label2 w-full rounded-sm p-3 text-center font-normal text-gray-900">
            Content written by the Landlord is displayed through automatic
            translation.
          </div>
        </div>
      )}
      <section className="space-y-10 px-4 pb-10">
        <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-15">
          <MobileStructure
            roomCnt={roomCnt}
            bathroomCnt={bathroomCnt}
            cookroomCnt={cookroomCnt}
            sittingroomCnt={sittingroomCnt}
            sharedRoom={sharedRoom}
            sharedBathroom={sharedBathroom}
            sharedCookroom={sharedCookroom}
            className="order-1"
          />
          <MobileSharedPlaceMessage
            sharedRoom={sharedRoom}
            sharedBathroom={sharedBathroom}
            sharedCookroom={sharedCookroom}
            className="order-2 md:order-3 md:col-span-2"
          />
          <MobileDuplexStructureMessage
            duplexStructure={duplexStructure}
            className="order-3 md:order-4 md:col-span-2"
          />
          <MobileProperty
            propertyType={propertyType}
            pyeongSize={pyeongSize}
            squareMeterSize={squareMeterSize}
            className="order-4 mt-4 md:order-2 md:mt-0"
          />
        </div>
        <MobileFacilities
          hasElevator={hasElevator}
          parkingType={parkingType}
          parkingNotice={parkingNotice}
        />
      </section>
      <Separator color="light-gray" size="large" />
      <MobileIntroduction description={description} />
      <Separator color="light-gray" size="large" />
      <MobileLocation
        addrStreet={addrStreet}
        lat={lat}
        lng={lng}
        subwayStations={subwayStations}
      />
      <MobileTransportation transportation={transportation} />
      <Separator color="light-gray" size="large" />
      <MobileRoomOption
        basicOptions={basicOptions}
        additionalOptions={additionalOptions}
        missingOptions={missingOptions}
      />
      <Separator color="light-gray" size="large" />
      {(hasUsageGuide || hasAdditionalDescription) && (
        <>
          <div className="space-y-10 px-4 py-10">
            {hasUsageGuide && <MobileUsageGuide usageGuide={usageGuide} />}
            {hasAdditionalDescription && (
              <MobileAdditionalDescription
                additionalDescription={additionalDescription}
              />
            )}
          </div>
          <Separator color="light-gray" size="large" />
        </>
      )}
      <MobileManagementFee
        includeElectricity={includeElectricity}
        includeWater={includeWater}
        includeGas={includeGas}
        includeInternet={includeInternet}
        managementFeeDescription={managementFeeDescription}
      />
    </div>
  )
}
